import { GetServerSideProps, NextPage } from 'next'
import dynamic from 'next/dynamic'
import { ParsedUrlQuery } from 'querystring'
import { unstable_serialize } from 'swr'
import { client } from '~/api/client'
import { getFoldersFolderIdElementsV1, getFoldersFolderIdV1 } from '~/api/gen'
import { Layout } from '~/components/Layout'
import { Meta } from '~/components/Meta'
import { FolderDetail } from '~/features/FolderDetail/FolderDetail'
import { FETCH_COUNT_PER_PAGE } from '~/features/FolderDetail/constants'
import { useFolderDetail } from '~/features/FolderDetail/hooks/useFolderDetail'
import { useFolderElements } from '~/features/FolderDetail/hooks/useFolderElements'
import { usePvCount } from '~/features/FolderDetail/hooks/usePvCount'
import {
  FilterType,
  filterValueTypeMap,
} from '~/features/FolderDetail/lib/filter'
import { getBreadcrumb } from '~/features/FolderDetail/lib/getBreadcrumb'
import { getMetadata } from '~/features/FolderDetail/lib/getMetadata'
import { assertSuccess } from '~/lib/assertSuccess'
import { FOLDER_DETAIL_HEADER_ADJUST_URL } from '~/lib/constants'
import { getPageNumberFromQueryValue } from '~/lib/getPageNumberFromQueryValue'

const GoogleAdScripts = dynamic(
  () => import('~/features/shared/ad/components/GoogleAdScripts'),
  { ssr: false }
)
class ValidationError extends Error {}

export type Props = {
  folderId: number
  pageNumber: number
  filterType: FilterType
}

const validatePageParams = (
  params: ParsedUrlQuery
): { userName: string; folderId: number } => {
  const userName = params.username
  if (typeof userName !== 'string' || userName === '') {
    throw new ValidationError('Unexpected username')
  }
  const folderId = Number(params.folderId)
  if (!Number.isFinite(folderId)) {
    throw new ValidationError('Unexpected folderId')
  }
  return { userName, folderId }
}

const getFilterTypeFromQueryValue = (
  str: ParsedUrlQuery[string]
):
  | { isValid: false; filterType?: never }
  | { isValid: true; filterType: FilterType } => {
  // eslint-disable-next-line no-restricted-syntax, @typescript-eslint/restrict-template-expressions
  const s = `${str}`
  if (Object.keys(filterValueTypeMap).includes(s)) {
    return { isValid: true, filterType: filterValueTypeMap[s] }
  }
  return { isValid: false }
}
// eslint-disable-next-line complexity
export const FolderDetailPage: NextPage<Props> = ({
  folderId,
  pageNumber,
  filterType,
}) => {
  usePvCount(folderId)
  const detail = useFolderDetail(folderId)
  const elements = useFolderElements(folderId, pageNumber, filterType)
  if (detail === undefined || elements === undefined)
    throw new Error(`folderDetail or folderElements are undefined`)
  const meta = getMetadata({
    member: detail.member,
    folderId: detail.id,
    folderName: detail.name,
    noIndex:
      detail.aggregate === null || detail.aggregate.active_element_count <= 1,
    firstImageUrl:
      elements.folder_elements[0]?.folder_coordinate?.coordinate.image
        .url_500 ?? elements.folder_elements[0]?.folder_item?.image?.url_500,
  })
  const shop = detail.member?.shop
    ? { id: detail.member.shop.id, name: detail.member.shop.name }
    : undefined
  return (
    <Layout
      breadcrumbList={getBreadcrumb(
        detail.member.user_name,
        detail.member.nick_name,
        detail.name,
        shop
      )}
      headerProps={{
        headerType: 'border',
        appLink: meta.appUrl,
        hideAppIcon: true,
      }}
      abstractUrl="wear.jp/username/favorite/favoriteid/"
      appDownloadUrl={FOLDER_DETAIL_HEADER_ADJUST_URL}
    >
      <Meta {...meta} />
      <GoogleAdScripts
        pages={[{ label: 'pc:folder:detail' }, { label: 'sp:folder:detail' }]}
      />
      <FolderDetail
        detail={detail}
        folderElements={elements.folder_elements}
        maxCount={elements.max_count}
        pageNumber={pageNumber}
        filterType={filterType}
      />
    </Layout>
  )
}

export const getServerSideProps: GetServerSideProps<Props> = async ({
  query,
  params = {},
}) => {
  try {
    const { userName, folderId } = validatePageParams(params)
    const { filterType, isValid: filterValid } = getFilterTypeFromQueryValue(
      query.dp_type
    )
    const { pageNumber, isValid: pageValid } = getPageNumberFromQueryValue(
      query.pageno
    )
    if (!pageValid || !filterValid) {
      return {
        redirect: {
          statusCode: 302,
          destination: `/${userName}/favorite/${folderId}`,
        },
      }
    }
    const [folderInfoResult, folderElementsResult] = await Promise.all([
      getFoldersFolderIdV1(client, {
        parameter: { folder_id: folderId },
      }),
      getFoldersFolderIdElementsV1(client, {
        parameter: {
          element_type: filterType,
          folder_id: folderId,
          pageno: pageNumber,
          pagesize: FETCH_COUNT_PER_PAGE,
        },
      }),
    ])
    if (
      folderElementsResult.raw.status === 404 ||
      folderInfoResult.raw.status === 404
    ) {
      return { notFound: true }
    }
    assertSuccess(folderInfoResult)

    const folderInfo = folderInfoResult.data
    assertSuccess(folderElementsResult)

    if (
      folderInfo.folder.member.user_name.toLowerCase() !==
      userName.toLowerCase()
    ) {
      return {
        redirect: {
          statusCode: 301,
          destination: `/${folderInfo.folder.member.user_name}/favorite/${folderId}/`,
        },
      }
    }
    return {
      props: {
        fallback: {
          [unstable_serialize(['getFoldersFolderIdV1', folderId])]:
            folderInfo.folder,
          [unstable_serialize([
            'getFoldersFolderIdElementsV1',
            folderId,
            pageNumber,
            filterType,
          ])]: folderElementsResult.data,
        },
        folderId,
        pageNumber,
        filterType,
      },
    }
  } catch (err) {
    if (err instanceof ValidationError) {
      console.error(err)
      return { notFound: true }
    }
    throw new Error('Something wrong!')
  }
}

export default FolderDetailPage
